import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  PlayCircle,
  StopCircle,
  CheckCircle,
  AlertTriangle,
  Users,
  ClipboardList,
  Wrench,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabCount } from '@/components/common/TabCount';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  PageHeader,
  StatusBadge,
  MetricCard,
  DataTable,
  Column,
  KPIStrip,
} from '@/components/common';
import { EquipmentBreadcrumb } from '@/components/equipment';
import {
  equipmentApi,
  EquipmentDetail as EquipmentDetailType,
  DowntimeEvent,
  RecordDowntimeDto,
  DowntimeReason,
  EquipmentBreadcrumb as BreadcrumbType,
} from '@/api/services/equipment.api';
import { getErrorMessage } from '@/api/client';
import { formatDate as formatDateValue, formatDateTime, toTitleCase, pluralize } from '@/lib/utils';

// Delegates to the shared formatter. This used to call toLocaleDateString()
// directly, which follows the browser's locale — so the same asset read
// 04/05/2026 here and "May 4, 2026" on the list, and neither told you which
// of the two numbers was the month.
const formatDate = (value: string | null | undefined) => (value ? formatDateValue(value) : null);

/**
 * What the asset is, and where it stands against its service dates.
 *
 * These fields used to sit in a full-width grid above the history tabs. The
 * grid was compact, but it meant scrolling past the identity to reach the
 * downtime record and back again to check an operating-hours figure against it.
 * A rail keeps both on screen.
 */
function EquipmentRail({ equipment }: { equipment: EquipmentDetailType }) {
  const daysUntil = (d: string | null | undefined) =>
    d ? Math.ceil((new Date(d).getTime() - Date.now()) / 86_400_000) : null;

  const untilService = daysUntil(equipment.nextServiceDate);
  const untilWarranty = daysUntil(equipment.warrantyExpiry);

  return (
    <>
      <RailCard title="Service" icon={<Wrench className="h-4 w-4 text-muted-foreground" />}>
        <DetailList
          items={[
            { label: 'Last service', value: formatDate(equipment.lastServiceDate) || '—' },
            {
              label: 'Next service',
              value: formatDate(equipment.nextServiceDate) || '—',
              // Overdue is the one state worth colouring: a date on its own does
              // not say whether it has passed.
              tone:
                untilService == null
                  ? 'default'
                  : untilService < 0
                    ? 'destructive'
                    : untilService <= 30
                      ? 'warning'
                      : 'default',
            },
            {
              label: 'Warranty',
              value: formatDate(equipment.warrantyExpiry) || '—',
              tone:
                untilWarranty == null
                  ? 'default'
                  : untilWarranty < 0
                    ? 'destructive'
                    : untilWarranty <= 30
                      ? 'warning'
                      : 'default',
            },
            {
              label: 'Operating hours',
              value:
                equipment.operatingHours != null
                  ? `${equipment.operatingHours.toLocaleString()} hrs`
                  : '—',
            },
          ]}
        />
        {untilService != null && untilService < 0 && (
          <p className="mt-3 text-xs text-status-destructive-foreground">
            Service overdue by {Math.abs(untilService)} {pluralize(Math.abs(untilService), 'day')}.
          </p>
        )}
        {untilWarranty != null && untilWarranty < 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            Warranty expired {Math.abs(untilWarranty)} {pluralize(Math.abs(untilWarranty), 'day')}{' '}
            ago — repairs are billable.
          </p>
        )}
      </RailCard>

      <RailCard title="Identity" icon={<Package className="h-4 w-4 text-muted-foreground" />}>
        <DetailList
          items={[
            { label: 'Category', value: toTitleCase(equipment.category) },
            { label: 'Location', value: equipment.location.name },
            {
              label: 'Condition',
              value: (
                <StatusBadge
                  status={equipment.condition.toLowerCase()}
                  label={equipment.condition}
                />
              ),
            },
            { label: 'Manufacturer', value: equipment.manufacturer || '—' },
            { label: 'Model', value: equipment.model || '—' },
            {
              label: 'Serial number',
              // A missing serial number on an asset register is worth seeing
              // rather than hiding, which is why this row never drops out.
              value: equipment.serialNumber ? (
                <span className="font-mono">{equipment.serialNumber}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
            },
            { label: 'Purchased', value: formatDate(equipment.purchaseDate) || '—' },
            {
              label: 'Purchase price',
              value: equipment.purchasePrice ? `$${equipment.purchasePrice.toLocaleString()}` : '—',
            },
          ]}
        />
      </RailCard>
    </>
  );
}

const downtimeReasons: DowntimeReason[] = [
  'SCHEDULED_MAINTENANCE',
  'UNSCHEDULED_MAINTENANCE',
  'BREAKDOWN',
  'INSPECTION',
  'CALIBRATION',
  'OPERATOR_ERROR',
  'ENVIRONMENTAL',
  'OTHER',
];

export default function EquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<EquipmentDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbType[]>([]);

  // Downtime modal
  const [downtimeOpen, setDowntimeOpen] = useState(false);
  const [downtimeData, setDowntimeData] = useState<RecordDowntimeDto>({
    startTime: new Date().toISOString().slice(0, 16),
    reason: 'SCHEDULED_MAINTENANCE',
    description: '',
  });
  const [savingDowntime, setSavingDowntime] = useState(false);

  // Active downtime to end
  const [activeDowntime, setActiveDowntime] = useState<DowntimeEvent | null>(null);

  const fetchEquipment = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [equipmentRes, breadcrumbsRes] = await Promise.all([
        equipmentApi.getById(id),
        equipmentApi.getBreadcrumbs(id),
      ]);

      setEquipment(equipmentRes.data);
      setBreadcrumbs(breadcrumbsRes.data);

      // Find active downtime
      const active = equipmentRes.data.downtimeEvents?.find((e) => !e.endTime);
      setActiveDowntime(active || null);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/equipment');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchEquipment();
  }, [id, fetchEquipment]);

  const handleRecordDowntime = async () => {
    if (!equipment) return;

    try {
      setSavingDowntime(true);
      await equipmentApi.recordDowntime(equipment.id, downtimeData);
      toast.success('Downtime recorded successfully');
      setDowntimeOpen(false);
      setDowntimeData({
        startTime: new Date().toISOString().slice(0, 16),
        reason: 'SCHEDULED_MAINTENANCE',
        description: '',
      });
      fetchEquipment();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingDowntime(false);
    }
  };

  const handleEndDowntime = async () => {
    if (!equipment || !activeDowntime) return;

    try {
      setSavingDowntime(true);
      await equipmentApi.endDowntime(equipment.id, activeDowntime.id, {});
      toast.success('Downtime ended successfully');
      fetchEquipment();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingDowntime(false);
    }
  };

  const downtimeColumns: Column<DowntimeEvent>[] = [
    {
      key: 'reason',
      header: 'Reason',
      render: (event) => (
        <div>
          <span className="font-medium">{event.reason.replace(/_/g, ' ')}</span>
          {event.description && (
            <p className="text-xs text-muted-foreground">{event.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'startTime',
      header: 'Start Time',
      render: (event) => <>{formatDateTime(event.startTime)}</>,
    },
    {
      key: 'endTime',
      header: 'End Time',
      render: (event) =>
        event.endTime ? (
          <>{formatDateTime(event.endTime)}</>
        ) : (
          <StatusBadge status="critical" label="Active" />
        ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (event) => {
        const duration = event.endTime
          ? (new Date(event.endTime).getTime() - new Date(event.startTime).getTime()) /
            (1000 * 60 * 60)
          : null;
        return <>{duration !== null ? `${duration.toFixed(1)} hrs` : '-'}</>;
      },
    },
    {
      key: 'reportedBy',
      header: 'Reported By',
      render: (event) => <>{event.reportedBy.fullName}</>,
    },
  ];

  type WorkOrderRow = EquipmentDetailType['workOrders'][number];
  const workOrderColumns: Column<WorkOrderRow>[] = [
    {
      key: 'workOrderNumber',
      header: 'WO Number',
      render: (wo) => <span className="font-medium">{wo.workOrderNumber}</span>,
    },
    { key: 'title', header: 'Title' },
    {
      key: 'type',
      header: 'Type',
      render: (wo) => <>{wo.type.replace(/_/g, ' ')}</>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (wo) => <StatusBadge status={wo.status} />,
    },
    {
      key: 'scheduledStart',
      header: 'Scheduled',
      render: (wo) => <>{wo.scheduledStart ? formatDateValue(wo.scheduledStart) : '-'}</>,
    },
  ];

  type MaintenanceRow = EquipmentDetailType['maintenanceSchedules'][number];
  const maintenanceColumns: Column<MaintenanceRow>[] = [
    {
      key: 'name',
      header: 'Schedule Name',
      render: (schedule) => <span className="font-medium">{schedule.name}</span>,
    },
    {
      key: 'frequency',
      header: 'Frequency',
      render: (schedule) => <>{schedule.frequency.replace(/_/g, ' ')}</>,
    },
    {
      key: 'nextDue',
      header: 'Next Due',
      render: (schedule) => <>{formatDateValue(schedule.nextDue)}</>,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (schedule) => <Badge variant="secondary">{schedule.priority}</Badge>,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48 md:col-span-2" />
        </div>
      </div>
    );
  }

  if (!equipment) {
    return null;
  }

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Equipment', href: '/equipment' },
            { label: equipment.assetNumber },
          ]}
          title={equipment.name}
          description={`Asset #${equipment.assetNumber}`}
          // Status and condition read on the title line, as they do on an
          // inventory item, rather than only inside the details panel below.
          titleAdornment={
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={equipment.status} />
              {equipment.condition && (
                <StatusBadge
                  status={equipment.condition.toLowerCase()}
                  label={equipment.condition}
                />
              )}
            </div>
          }
          actions={
            <div className="flex gap-2">
              {activeDowntime ? (
                <Button onClick={handleEndDowntime} disabled={savingDowntime}>
                  <StopCircle className="mr-2 h-4 w-4" />
                  End Downtime
                </Button>
              ) : (
                <Button onClick={() => setDowntimeOpen(true)}>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Record Downtime
                </Button>
              )}
            </div>
          }
        />
      </motion.div>

      {/* Equipment Metrics */}
      <div className="mt-6">
        <KPIStrip columns={4}>
          <MetricCard
            label="Equipment Status"
            value={equipment.status.replace(/_/g, ' ')}
            sentiment={
              equipment.status === 'AVAILABLE' || equipment.status === 'IN_USE'
                ? 'success'
                : equipment.status === 'DISPOSED' || equipment.status === 'RETIRED'
                  ? 'danger'
                  : 'warning'
            }
            icon={<CheckCircle className="h-5 w-5" />}
          />
          <MetricCard
            label="Sub-Equipment"
            value={equipment.children?.length || 0}
            sentiment="info"
            icon={<Users className="h-5 w-5" />}
          />
          <MetricCard
            label="Downtime Status"
            value={activeDowntime ? 'Active' : 'Operational'}
            sentiment={activeDowntime ? 'danger' : 'success'}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
          <MetricCard
            label="Work Orders"
            value={equipment.workOrders?.length || 0}
            sentiment="neutral"
            icon={<ClipboardList className="h-5 w-5" />}
          />
        </KPIStrip>
      </div>

      {/* Breadcrumb — only once there is an ancestor to walk back to. A single
          crumb just repeats the page title, and a card around it costs ~80px
          of vertical space to say nothing. */}
      {breadcrumbs.length > 1 && (
        <motion.div variants={fadeUp} className="mt-4">
          <EquipmentBreadcrumb breadcrumbs={breadcrumbs} />
        </motion.div>
      )}

      {/* Sub-equipment. There is no matching "Parent" card: the breadcrumb
          above already names the parent and links to it, so a second copy in
          card form said the same thing twice. */}
      {equipment.children && equipment.children.length > 0 && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card>
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground">Sub-Equipment</h3>
              <CardDescription>
                {equipment.children.length} component{equipment.children.length > 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 sm:grid-cols-2">
                {equipment.children.slice(0, 6).map((child) => (
                  <button
                    key={child.id}
                    onClick={() => navigate(`/equipment/${child.id}`)}
                    className="w-full rounded-lg bg-muted p-3 text-left transition-colors hover:bg-accent"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{child.name}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {child.assetNumber} • {child.category.replace(/_/g, ' ')}
                        </p>
                      </div>
                      {/* StatusBadge lowercases and maps spaces itself; turning
                            the underscores into hyphens here stopped IN_USE
                            matching a variant, so it rendered untinted. */}
                      <StatusBadge status={child.status} size="sm" />
                    </div>
                  </button>
                ))}
                {equipment.children.length > 6 && (
                  <button
                    onClick={() => navigate('/equipment/hierarchy')}
                    className="w-full p-3 text-center text-sm text-primary hover:underline"
                  >
                    View all {equipment.children.length} sub-equipment
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Two columns: the history is what you came to read, so it takes the
          main column. What the asset *is* — its identity, its service dates —
          is reference, and belongs in a rail where it stays beside a downtime
          record instead of several screens above it.

          This replaces the full-width specification grid. That grid was itself
          a fix for two half-empty cards, and it did carry every field in less
          height — but it put them where you had to scroll past them to reach
          the history, and then scroll back to check an operating-hours figure
          against the downtime you were reading. */}
      <motion.div variants={fadeUp} className="mt-6">
        <DetailLayout
          rail={<EquipmentRail equipment={equipment} />}
          main={
            <Tabs defaultValue="downtime" className="space-y-4">
              <TabsList>
                <TabsTrigger value="downtime">
                  Downtime History
                  <TabCount value={equipment.downtimeEvents?.length ?? null} />
                </TabsTrigger>
                <TabsTrigger value="workorders">
                  Work Orders
                  <TabCount value={equipment.workOrders?.length ?? null} />
                </TabsTrigger>
                <TabsTrigger value="maintenance">
                  Maintenance Schedules
                  <TabCount value={equipment.maintenanceSchedules?.length ?? null} />
                </TabsTrigger>
              </TabsList>

              <TabsContent value="downtime">
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Downtime Events</h3>
                    <CardDescription>Track equipment downtime and repairs</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DataTable<DowntimeEvent>
                      pagination
                      data={equipment.downtimeEvents}
                      columns={downtimeColumns}
                      emptyTitle="No downtime events recorded"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="workorders">
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Work Orders</h3>
                    <CardDescription>Work orders associated with this equipment</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DataTable<EquipmentDetailType['workOrders'][number]>
                      pagination
                      data={equipment.workOrders}
                      columns={workOrderColumns}
                      onRowClick={(wo) => navigate(`/work-orders/${wo.id}`)}
                      emptyTitle="No work orders found"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="maintenance">
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">
                      Maintenance Schedules
                    </h3>
                    <CardDescription>
                      Active maintenance schedules for this equipment
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DataTable<EquipmentDetailType['maintenanceSchedules'][number]>
                      pagination
                      data={equipment.maintenanceSchedules}
                      columns={maintenanceColumns}
                      emptyTitle="No maintenance schedules configured"
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          }
        />
      </motion.div>

      {/* Record Downtime Dialog */}
      <Dialog open={downtimeOpen} onOpenChange={setDowntimeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Downtime</DialogTitle>
            <DialogDescription>
              Record equipment downtime for maintenance or repair
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Select
                value={downtimeData.reason}
                onValueChange={(v: DowntimeReason) =>
                  setDowntimeData((prev) => ({ ...prev, reason: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {downtimeReasons.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime">Start Time</Label>
              <Input
                id="startTime"
                type="datetime-local"
                value={downtimeData.startTime}
                onChange={(e) =>
                  setDowntimeData((prev) => ({ ...prev, startTime: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={downtimeData.description || ''}
                onChange={(e) =>
                  setDowntimeData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Optional details about the downtime"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDowntimeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordDowntime} disabled={savingDowntime}>
              {savingDowntime ? 'Recording...' : 'Record Downtime'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
